<template>
    <div class="p-1 flex flex-col h-full" @click="activateFocus">
        <div class="flex flex-col justify-between mb-2 ml-2">
            <div class="flex items-center">
                <div class="text-lg grow truncate">
                    {{ subAccount.name }}
                    <sub-account-link tabindex="-1" :uuid="pickerOption.subAccountUuid" class="text-primary i-ph-arrow-square-out inline-block -mb-0.5" />
                </div>
                <div class="text-xs">
                    <kbd class="kbd kbd-xs">mellomrom</kbd> for å velge
                </div>
            </div>
        </div>
        <recycle-scroller
            id="recycleScroller"
            v-slot="{item}"
            ref="recycleScrollerRef"
            :items="rows"
            :item-size="62"
            key-field="uuid"
            class="h-fit outline-none max-h-80"
            :tabindex="recycleScrollerTabindex"
            @keydown.down="highlightNext"
            @keydown.up="highlightPrevious"
            @keydown.left.exact.prevent="handleArrowLeft"
            @keydown.space="toggleHighlightedPost"
            @keydown.tab="onEnter"
            @keydown.enter="onEnter"
        >
            <account-picker-post-row
                :selected="postIsSelected(item)"
                :highlighted="highlightedPost === getRowIndex(item)"
                :post="item"
                class="even:bg-secondary"
                :class="['sacc-post-' + getRowIndex(item), {'bg-base-200/50': getRowIndex(item) % 2}]"
                @toggle-post="togglePost(item)"
            />
        </recycle-scroller>
        <div class="grow"/>
        <div class="items-baseline my-2 w-full">
            <div class="flex gap-6 justify-between items-center mb-2">
                <span>Saldo: </span><span class="">{{ formatCurrency(currentBalance) }}</span>
            </div>
            <div v-if="selectedPostCount" class="flex gap-6 justify-between items-center">
                <span>Sum av valgte poster:</span>
                <button
                    class="btn btn-primary btn-sm"
                    :disabled="sumSelectedPosts === 0"
                    title="Bruk som beløp på bilagslinje"
                    @click="confirmSelectedPosts"
                >
                    <i class="i-ph-key-return block text-lg"/> {{ formatCurrency(Math.abs(sumSelectedPosts)) }}
                </button>
            </div>
            <div v-else class="flex justify-end">
                <button class="btn btn-primary btn-sm" @click="confirmSelectedPosts">
                    <i class="i-ph-key-return block text-lg"/> Velg konto uten poster
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {AccountOption, accountTypeToRelationType, FOCUS_SIDE} from '@/account-picker/accountPicker.service'
import {computed, ref, watch, nextTick} from 'vue'
import _ from 'lodash'
import {findSubAccountByUuid, useSubAccounts} from '@/subaccount/subAccount.service'
import {SubAccountLine} from '@/subaccount/outstandingBalance/SubAccountLine'
//@ts-ignore
import {RecycleScroller} from 'vue-virtual-scroller'
import {formatCurrency} from '@/services/formatter.service'
import SubAccountLink from '@/subaccount/components/SubAccountLink.vue'
import AccountPickerPostRow from '@/account-picker/AccountPickerPostRow.vue'

const {fetchSubAccountLedger, fetchSubAccountLines} = useSubAccounts()

const props = defineProps({
    pickerOption: {
        type: Object as () => AccountOption & { subAccountUuid: string },
        required: true
    }
})

const emit = defineEmits(['onSelectionComplete', 'onSelectItem', 'focusScroller'])

const onEnter = () => {
    emit('onSelectionComplete')
}

const focusSide = defineModel<FOCUS_SIDE>('focusSide', {required: true})

const isFocused = computed({
    get: () => focusSide.value === FOCUS_SIDE.RIGHT,
    set: (value) => focusSide.value = value ? FOCUS_SIDE.RIGHT : FOCUS_SIDE.LEFT
})

const subAccountPosts = defineModel<SubAccountLine[]>('subAccountPosts', {default: () => []})

const subAccountLines = defineModel<string[]>('subAccountLines')

const rows = ref<SubAccountLine[]>([])

const currentBalance = ref(0)

const selectedPostCount = computed(() => {
    return subAccountPosts.value.length
})

const sumSelectedPosts = computed(() => {
    return subAccountPosts.value.reduce((acc, curr) => acc + curr.remainingAmount, 0)
})

const highlightedPost = ref<number>(-1)

const recycleScrollerRef = ref<InstanceType<RecycleScroller>>(null)
const recycleScrollerTabindex = ref(-1)

const getRowIndex = (item: SubAccountLine) => rows?.value?.findIndex(i => i.uuid === item.uuid)

const handleArrowLeft = () => {
    recycleScrollerTabindex.value = -1
    isFocused.value = false
    emit('focusScroller')
}

const highlightPrevious = (event: Event) => {
    event.preventDefault()
    if (highlightedPost.value === 0) return
    highlightedPost.value -= 1
    ensurePostVisibleInScroller()
}
const highlightNext = (event: Event) => {
    event.preventDefault()
    if (highlightedPost.value === rows.value.length - 1) return
    highlightedPost.value += 1
    ensurePostVisibleInScroller()
}

const ensurePostVisibleInScroller = () => {
    const index = highlightedPost.value
    if (index === -1) return
    const item = recycleScrollerRef?.value?.$el.querySelector(`.sacc-post-${index}`)
    if (item) {
        item.scrollIntoView({
            block: 'nearest',
            inline: 'nearest'
        })
    }
}

const highlightFirst = () => highlightedPost.value = 0

const activateFocus = () => {
    isFocused.value = true
    highlightFirst()
    recycleScrollerTabindex.value = 0
    nextTick(() => {
        recycleScrollerRef?.value?.$el.focus()
    })
}

const postIsSelected = (item: SubAccountLine) => {
    return !!subAccountPosts.value.find(i => i.uuid === item.uuid)
}

const toggleHighlightedPost = (event: Event) => {
    event.preventDefault()
    togglePost(rows.value[highlightedPost.value])
}

const togglePost = (item: SubAccountLine) => {
    const indexofExisting = subAccountPosts.value.findIndex(i => i.uuid === item.uuid)
    if (indexofExisting === -1) {
        subAccountPosts.value = [...subAccountPosts.value, item]
    } else {
        subAccountPosts.value = subAccountPosts.value.filter(i => i.uuid !== item.uuid)
    }
    emit('onSelectItem')
}

const subAccountLedgerFilter = computed(() => ({
    end: new Date(),
    isOpen: true
}))

const debouncedSubAccountFetcher = _.debounce(async () => {
    const {subAccountUuid, accountType} = props.pickerOption
    fetchSubAccountLedger(
        accountTypeToRelationType(accountType),
        subAccountUuid,
        subAccountLedgerFilter.value
    ).then((ledger) => {
        currentBalance.value = ledger?.outgoing || 0
    })

    const data = await fetchSubAccountLines(
        props.pickerOption.subAccountUuid,
        subAccountLedgerFilter.value
    )
    rows.value = data.content

    if (subAccountLines.value && !subAccountPosts.value.length) {
        subAccountPosts.value = subAccountLines.value
            .map(lineUuid => data.content.find(post => post.uuid === lineUuid))
            .filter(Boolean) as SubAccountLine[]
    }

}, 200, {trailing: true})

watch(isFocused, () => {
    if (!isFocused.value) return
    debouncedSubAccountFetcher()
})

const confirmSelectedPosts = onEnter

const subAccount = computed(() => {
    return findSubAccountByUuid(props.pickerOption.subAccountUuid)
})

defineExpose({
    highlightFirst,
    activateFocus
})

</script>

<style>



</style>